import { forwardRef, Fragment } from "react";
import { produce } from "immer";
import { CheckboxGroupContext, CheckboxContext } from "react-aria-components";
import { useDesignTokens } from "@boi/react-design-tokens";
import {
  CoreCheckboxGroup,
  type PropsWithOptionalTestID,
} from "@boi/components-core";

import * as S from "./NestedCheckboxGroup.styled";
import { ReactAriaProviders } from "../ReactAriaProviders";
import { InputWrapper } from "../InputWrapper";
import { InputAlerts, type InputAlertsProps } from "../InputAlerts";
import { InputLabelTopV2, type InputLabelTopV2Props } from "../InputLabelTopV2";
import {
  CheckboxGroupBaseV2,
  type CheckboxGroupBaseV2Props,
} from "../CheckboxGroupBaseV2";
import { CheckboxV2, type CheckboxV2Props } from "../CheckboxV2";

type Input = Omit<CheckboxV2Props, "onChange"> &
  Required<
    Pick<CheckboxV2Props, "labelProps" | "id" | "value" | "isSelected">
  > & {
    children?: Input[];
  };

export type NestedCheckboxGroupProps = PropsWithOptionalTestID &
  Omit<CheckboxGroupBaseV2Props, "value" | "onChange"> & {
    labelProps?: Omit<InputLabelTopV2Props, "children">;
    alerts?: InputAlertsProps["alerts"];
    inputs: Input[];
    onChange: (inputs: Input[]) => void;
  };

const NestedCheckboxGroup = forwardRef<
  HTMLDivElement,
  NestedCheckboxGroupProps
>((props, ref) => {
  const {
    labelProps,
    alerts,
    inputs,
    onChange,
    testID = "nested-checkbox-group",
    ...otherProps
  } = props;

  const { tokens } = useDesignTokens();
  const { checkboxGroup: checkboxGroupTokens } = tokens;

  const value = inputs
    .filter((input) => input.isSelected)
    .map((input) => input.value);

  const handleChange = (input: Input) => {
    const updatedInputs = produce(inputs, (draft) => {
      CoreCheckboxGroup.updateInputs(draft, input.id, "isSelected");
    });

    onChange(updatedInputs);
  };

  return (
    <ReactAriaProviders
      externallyLabelledBy={props["aria-labelledby"]}
      externallyDescribedBy={props["aria-describedby"]}
      labelProps={labelProps}
      alerts={alerts}
    >
      {({ labelledBy, describedBy }) => (
        <InputLabelTopV2 ref={ref} {...labelProps}>
          <InputWrapper
            gap={checkboxGroupTokens.gap}
            alert={alerts && <InputAlerts alerts={alerts} />}
          >
            <CheckboxGroupContext.Provider
              value={{
                isDisabled: otherProps.isDisabled,
                isInvalid: otherProps.isInvalid,
              }}
            >
              <CheckboxContext.Provider
                value={{ "aria-describedby": describedBy }}
              >
                <CheckboxGroupBaseV2
                  {...otherProps}
                  value={value}
                  aria-labelledby={labelledBy}
                  aria-describedby={describedBy}
                  data-testid={testID}
                >
                  {inputs.map((input) => (
                    <Fragment key={input.id}>
                      <CheckboxV2
                        {...input}
                        onChange={() => {
                          handleChange(input);
                        }}
                      />

                      {input.children && (
                        <RecursiveCheckboxGroup
                          input={input}
                          handleChange={handleChange}
                          describedBy={describedBy}
                        />
                      )}
                    </Fragment>
                  ))}
                </CheckboxGroupBaseV2>
              </CheckboxContext.Provider>
            </CheckboxGroupContext.Provider>
          </InputWrapper>
        </InputLabelTopV2>
      )}
    </ReactAriaProviders>
  );
});

NestedCheckboxGroup.displayName = "NestedCheckboxGroup";

type RecursiveCheckboxGroupProps = {
  input: Input;
  handleChange: (input: Input) => void;
  describedBy?: string;
};

function RecursiveCheckboxGroup(props: RecursiveCheckboxGroupProps) {
  const { input, handleChange, describedBy } = props;

  if (!input.children) {
    return null;
  }

  const value = input.children
    .filter((nestedInput) => nestedInput.isSelected)
    .map((nestedInput) => nestedInput.value);

  return (
    <S.NestedCheckboxGroup
      value={value}
      aria-label={input.labelProps.label}
      aria-describedby={describedBy}
    >
      {input.children.map((nestedInput) => (
        <Fragment key={nestedInput.id}>
          <CheckboxV2
            {...nestedInput}
            onChange={() => {
              handleChange(nestedInput);
            }}
          />

          {nestedInput.children && (
            <RecursiveCheckboxGroup
              input={nestedInput}
              handleChange={handleChange}
            />
          )}
        </Fragment>
      ))}
    </S.NestedCheckboxGroup>
  );
}

export default NestedCheckboxGroup;
